import { memo, useEffect, useRef } from 'react';
import classnames from 'classnames';
import { useSpring, animated } from 'react-spring';
import { useMeasure } from 'react-use';
import { isMobileOnly as isMobile } from 'react-device-detect';

import ResolvedText from 'components/atoms/ResolvedText';

import { useMedia } from 'utils/hooks';

import type { ImageAlign } from 'utils/types/customization';

import styles from './index.css';

export interface InfoDetailProp {
    className?: string;
    active?: boolean;
    images?: string[];
    imageType: 'default' | 'zoomable';
    imageAlign?: ImageAlign;
    label?: string;
    intro?: string;
    fixedLink?: {
        text: string;
        url: string;
    };
    onClickImage?: () => void;
    onCompleted?: () => void;
}

const InfoDetail: React.FC<InfoDetailProp> = ({
    className,
    active = true,
    images = [],
    imageType,
    imageAlign,
    label,
    intro,
    fixedLink,
    onClickImage = () => {},
}) => {
    const introRef = useRef<HTMLDivElement>(null);
    const [fixedLinkRef, { height: fixedLinkHeight }] = useMeasure<HTMLAnchorElement>();
    const { media, mediaScale } = useMedia();

    const { x } = useSpring({
        // x: active || media === 'phone' ? 0 : 400 * mediaScale,
        x: active || (media === 'phone' && isMobile) ? 0 : 400 * mediaScale,
    });

    useEffect(() => {
        introRef.current?.scrollTo(0, 0);
    }, [label, intro]);

    return (
        <animated.div
            className={classnames(
                styles.infoDetail,
                active && styles.active,
                isMobile && styles.mobile,
                images.length > 0 && styles.withImage,
                className,
            )}
            style={{ translateX: x }}
        >
            {images.length > 0 && (
                <div
                    className={classnames(styles.image, styles[imageAlign])}
                    style={{
                        // backgroundImage:
                        //     animeComplete || media === 'phone' ? `url(${images[0]})` : '',
                        backgroundImage: `url(${images[0]})`,
                    }}
                    onClick={() => {
                        if (imageType === 'zoomable') {
                            onClickImage();
                        }
                    }}
                    onKeyPress={() => {}}
                    role="menuitem"
                    tabIndex={0}
                    aria-label="image"
                    onContextMenu={(e) => {
                        e.preventDefault();
                        return false;
                    }}
                />
            )}
            {(label || intro) && (
                <div
                    ref={introRef}
                    className={classnames(
                        styles.intro,
                        images.length === 0 && styles.noImage,
                        fixedLink && styles.withLink,
                    )}
                    style={{
                        maxHeight: `calc(100% - ${
                            (images.length > 0 ? (217 + 25) * mediaScale : 0) +
                            (fixedLink ? fixedLinkHeight + 33 * mediaScale : 0)
                        }px)`,
                    }}
                >
                    {label}
                    {label && (
                        <>
                            <br />
                            <br />
                        </>
                    )}
                    <ResolvedText rawText={intro} />
                </div>
            )}
            {fixedLink && (
                <a
                    ref={fixedLinkRef}
                    className={classnames(styles.fixedLink, images.length > 0 && styles.withImage)}
                    href={fixedLink.url}
                    target="_blank"
                    rel="noreferrer noopener"
                >
                    {fixedLink.text}
                </a>
            )}
        </animated.div>
    );
};

export default memo(InfoDetail);
